import clsx from "clsx";
import fnsFormat from "date-fns/format";
import React, { FC, useEffect, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { get } from "lodash";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useRouteMatch } from "react-router-dom";

import { useSelectorDataConfigurator } from "~/hooks/selectorDataConfigurator";
import { defaultSelectElem, getIndicatorName } from "~/libs/getData";
import {
  useGetGrRatingsListNameLazyQuery,
  useGetGrRatingsListNameQuery,
  useGetIndicatorsForSelectLazyQuery,
  useSetGrRatingMutation,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import { getYupSchemaForGrRatingForm } from "./lib";
import {
  RATING_TYPE,
  REQUIRED_INDICATORS,
  REQUIRED_INDICATORS_INPUT,
} from "./options";

import s from "./GrRatingForm.less";

interface IProps {
  setCloseModal: () => void;
  refetch;
  formType?: string;
  GRRrating?;
  prevElemId?: string;
  isEditable?: boolean;
  formId: string;
}

const GrRatingForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  GRRrating = {},
  prevElemId = "",
  isEditable = false,
  formId,
}) => {
  const { t } = useTranslation();
  const { params } = useRouteMatch();
  const isNotSelectedRequiredField = useRef(false);
  const {
    rowsForSelect,
    introspectionHandler,
    searchValue,
    dataRequestLoading,
  } = useSelectorDataConfigurator({
    queryGenerator: useGetIndicatorsForSelectLazyQuery,
    responseDataGetterString: "IndicatorGetter.getIndicators.items",
    searchConfig: (searchText) => ({
      searchField: searchText.length > 0 ? "nameOrId" : "",
      searchText,
    }),
    customValueHandler: ({ id, ...ind }) => {
      return {
        value: id,
        label: `(${id}) ${getIndicatorName(ind)}`,
      };
    },
  });
  const {
    id,
    name,
    quarter1Option,
    quarter2Option,
    quarter1_2Option,
    quarter3Option,
    quarter2_3Option,
    quarter4Option,
    quarter3_4Option,
    yearValueOption,
    yearPercentOption,
    format,
    number,
    ParentId,
    PrevElementId,
    parentName,
    createdAt,
  } = GRRrating;

  const ratingTypeOptions = RATING_TYPE.map((option) => ({
    label: t(`editorGR.${option}`),
    value: option,
  }));

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      ...GRRrating,
      number: !!number ? Number(number) : null,
      type: GRRrating.type
        ? {
            label: t(`editorGR.${GRRrating.type}`),
            value: GRRrating.type,
          }
        : ratingTypeOptions[0],
      quarter1: quarter1Option,
      quarter2: quarter2Option,
      quarter1_2: quarter1_2Option,
      quarter3: quarter3Option,
      quarter2_3: quarter2_3Option,
      quarter4: quarter4Option,
      quarter3_4: quarter3_4Option,
      yearValue: yearValueOption,
      yearPercent: yearPercentOption,
      format: format ? format : 0,
      ParentId: defaultSelectElem(ParentId, parentName, "parent"),
      PrevElementId: defaultSelectElem(PrevElementId, name, "prev"),
      createdAt: fnsFormat(id ? new Date(createdAt) : new Date(), "dd.MM.yyyy"),
    },
    resolver: yupResolver(getYupSchemaForGrRatingForm(t)),
  });

  const [setGrVersionMutation] = useSetGrRatingMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });

  const RatingsId = prevElemId || PrevElementId;

  const [getListValueQuery, grListValue] = useGetGrRatingsListNameLazyQuery({
    variables: {
      onlyParent: false,
      RatingsId,
    },
  });

  useEffect(() => {
    RatingsId && getListValueQuery();
  }, [RatingsId]);

  const valueList = [
    {
      value: null,
      label: t("common.new"),
    },
    ...get(grListValue, "data.GlobalRatingGetter.getRatingsListName", []).map(
      ({ id, name }) => ({
        value: id,
        label: `(${id}) ${name}`,
      }),
    ),
  ];

  const grParentList = useGetGrRatingsListNameQuery({
    variables: {
      onlyParent: true,
      RatingsVersionId: params["id"],
    },
  });

  const parentList = [
    {
      value: null,
      label: t("common.without_parent"),
    },
    ...get(grParentList, "data.GlobalRatingGetter.getRatingsListName", []).map(
      ({ id, name }) => ({
        value: id,
        label: `(${id}) ${name}`,
      }),
    ),
  ];

  const indicatorsForSelect = [
    {
      value: null,
      label: t("common.without_indicator"),
    },
    ...rowsForSelect,
  ];

  const onSubmit = (data) => {
    const {
      name,
      nameEn,
      number,
      type,
      ParentId,
      PrevElementId,
      yearPercent,
      yearValue,
      quarter1,
      quarter2,
      quarter1_2,
      quarter3,
      quarter2_3,
      quarter4,
      quarter3_4,
      sort,
      round,
      format,
    } = data;

    const notSelectedIndicators = Object.keys(data).filter(
      (key) =>
        (REQUIRED_INDICATORS.includes(key) && data[key].value === null) ||
        (REQUIRED_INDICATORS_INPUT.includes(key) && data[key] === ""),
    );

    if (notSelectedIndicators.length > 0) {
      isNotSelectedRequiredField.current = true;
    } else {
      isNotSelectedRequiredField.current = false;
      setGrVersionMutation({
        variables: {
          id,
          name,
          nameEn,
          type: type.value ? type.value : type,
          number: number ? String(number) : "",
          ParentId: ParentId.value ? ParentId.value : null,
          PrevElementId: PrevElementId.value ? PrevElementId.value : null,
          yearPercent: yearPercent.value,
          yearValue: yearValue.value,
          quarter1: quarter1.value,
          quarter2: quarter2.value,
          quarter1_2: quarter1_2.value,
          quarter3: quarter3.value,
          quarter2_3: quarter2_3.value,
          quarter4: quarter4.value,
          quarter3_4: quarter3_4.value,
          round: round ? +round : null,
          sort: +sort,
          RatingsVersionId: params["id"],
          format: format ? +format : null,
        },
      });
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="number"
          control={control}
          label={t("editorGR.number")}
          error={errors["number"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="name"
          control={control}
          label={t("editorGR.name")}
          error={errors["name"]?.message}
          disabled={!isEditable}
        />
        <Field
          type="input"
          name="nameEn"
          control={control}
          label={t("editorGR.nameEn")}
          error={errors["nameEn"]?.message}
          disabled={!isEditable}
        />
        {REQUIRED_INDICATORS.map((field) => (
          <Field
            key={field}
            type="select"
            name={field}
            control={control}
            label={`${t(`editorGR.${field}`)} *`}
            options={indicatorsForSelect}
            labelInValue
            showSearch
            filterOption={false}
            onSearch={searchValue}
            onPopupScroll={introspectionHandler}
            loading={dataRequestLoading}
            error={errors[field]?.value?.message}
            disabled={!isEditable}
          />
        ))}
        <Field
          type="select"
          name="type"
          control={control}
          label={t("editorGR.type")}
          options={ratingTypeOptions}
          labelInValue
          error={errors["type"]?.message}
          disabled={!isEditable}
        />
        {["sort", "round", "format"].map((option) => (
          <Field
            key={option}
            type="input"
            name={option}
            control={control}
            label={t(`editorGR.${option}`)}
            error={errors[`${option}`]?.message}
            disabled={!isEditable}
          />
        ))}
        <Field
          type="select"
          name="ParentId"
          control={control}
          label={t("editorGR.ParentId")}
          options={parentList}
          labelInValue
          disabled={!isEditable}
        />
        <Field
          type="select"
          name="PrevElementId"
          control={control}
          label={t("editorGR.PrevElementId")}
          options={valueList}
          labelInValue
          disabled={PrevElementId === null ? true : !isEditable}
        />
        <Field
          type="input"
          name="createdAt"
          control={control}
          label={t("editorGR.createdAt")}
          disabled
        />
        <div
          className={clsx(s.footnote, {
            [s.footnote_none]: isNotSelectedRequiredField.current,
          })}
        >
          {t("q.asterisk")}
        </div>
      </Flex>
    </form>
  );
};
export default GrRatingForm;
